import { reflowImportedWidthTables } from '@/components/tiptap-editor/tiptap-node/table-node/lib/reflow-imported-width-tables';
import { registerInlineFullscreen } from '@/components/tiptap-editor/tiptap-ui/fullscreen-activity';
import { ZyFullscreenToggle } from '@/components/tiptap-editor/tiptap-ui/zyfullscreen-toggle';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import type { CollaborationPresenceUser } from '@/hooks/use-collaboration-presence';
import { forwardSnapshotCompareDecorationAttrs } from '@/lib/snapshot/snapshot-user';
import { cn } from '@/lib/utils';
import { extractAttributeChanges } from '@tiptap-pro/extension-snapshot-compare';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import type { Decoration } from '@tiptap/pm/view';
import { NodeViewWrapper, useCurrentEditor, type NodeViewWrapperProps } from '@tiptap/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import {
    useEditorPaneBounds,
    useFullscreenBoundaryContext,
    type EditorPaneBounds,
} from '../tiptap-editor/tiptap-ui/fullscreen-boundary-context';
import { NodePresenceIndicator } from './node-presence-indicator';

const DEFAULT_CONTENT_HEIGHT = 'h-96';
export const NODE_VIEW_BACKGROUND_CLASS = 'bg-white dark:bg-black';
export const DEFAULT_CONTENT_CLASS = 'px-6 ' + NODE_VIEW_BACKGROUND_CLASS;
const LABEL_BADGE_CLASS = 'absolute -top-3 left-4 rounded bg-gray-400 px-3 py-1 text-xs font-semibold text-white z-10';
const DEFAULT_FOOTER_CLASS = 'px-6 py-3 ' + NODE_VIEW_BACKGROUND_CLASS;
const DEFAULT_FULLSCREEN_CLASS = 'flex flex-col rounded-none border bg-white dark:bg-gray-900';
const PORTAL_FULLSCREEN_Z_INDEX = 55;

export type ZyNodeViewWrapperProps = NodeViewWrapperProps & {
    decorations?: readonly Decoration[];
};

function formatAttributeDiffValue(value: unknown): string {
    if (value == null) {
        return '—';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
}

function AttributeDiffHoverContent({
    before,
    after,
    changedAttributes,
}: {
    before: Record<string, unknown>;
    after: Record<string, unknown>;
    changedAttributes: string[];
}) {
    return (
        <div className="space-y-2 text-sm">
            <p className="font-semibold">Option changes</p>
            <ul className="space-y-1">
                {changedAttributes.map(key => (
                    <li
                        key={key}
                        className="break-all"
                    >
                        <span className="font-medium">{key}:</span>{' '}
                        <span className="text-muted-foreground">{formatAttributeDiffValue(before[key])}</span>
                        {' → '}
                        <span>{formatAttributeDiffValue(after[key])}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function ZyNodeViewWrapper({ decorations, className, style, children, ...props }: ZyNodeViewWrapperProps) {
    const forwarded = forwardSnapshotCompareDecorationAttrs(decorations);
    const { before, after, changedAttributes, isDiffing } = extractAttributeChanges(
        (decorations ?? []) as Parameters<typeof extractAttributeChanges>[0],
    );
    const showAttributeDiffPopup =
        forwarded['data-diff-diff-content-type'] === 'attribute-only' && isDiffing && changedAttributes.length > 0;

    const nodeView = (
        <NodeViewWrapper
            {...props}
            className={cn(className, forwarded.className)}
            style={{ ...style, ...forwarded.style }}
            data-diff-type={forwarded['data-diff-type']}
            data-diff-user-id={forwarded['data-diff-user-id']}
            data-diff-user-name={forwarded['data-diff-user-name']}
        >
            {children}
        </NodeViewWrapper>
    );

    if (!showAttributeDiffPopup) {
        return nodeView;
    }

    return (
        <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>{nodeView}</HoverCardTrigger>
            <HoverCardContent className="w-auto max-w-md">
                <AttributeDiffHoverContent
                    before={before}
                    after={after}
                    changedAttributes={changedAttributes}
                />
            </HoverCardContent>
        </HoverCard>
    );
}

export interface CommonNodeViewWrapperProps {
    id?: string;
    label: ReactNode;
    selected: boolean;
    decorations?: readonly Decoration[];
    presenceUsers?: CollaborationPresenceUser[];
    settingsPanel?: ReactNode;
    contentClassName?: string;
    containerClassName?: string;
    containerProps?: React.HTMLAttributes<HTMLDivElement> & Record<string, string | undefined>;
    children: ReactNode;
    footer?: ReactNode;
    footerClassName?: string;
    position?: 'relative' | 'fixed';
    presenceTracking?: {
        collabProvider: TiptapCollabProvider | null;
        nodeId: string | null;
        nodeType: string;
        isActive?: boolean;
    };
    enableFullscreen?: boolean;
    fullscreenContent?: ReactNode;
    fullscreenContainerClassName?: string;
    exitFullscreenRef?: React.RefObject<(() => void) | undefined>;
    fullscreenMode?: 'portal' | 'inline';
    contentHeightClassName?: string;
    fullscreenScope?: 'editor' | 'viewport';
    isFullscreen?: boolean;
    onFullscreenChange?: (isFullscreen: boolean) => void;
}

function getFullscreenPositionStyle(scope: 'editor' | 'viewport', bounds: EditorPaneBounds | null): React.CSSProperties | undefined {
    if (scope === 'viewport') {
        return {
            position: 'fixed',
            inset: 0,
            zIndex: 51,
        };
    }
    if (!bounds) return undefined;
    return {
        position: 'fixed',
        top: bounds.top,
        left: bounds.left,
        width: bounds.width,
        height: bounds.height,
        zIndex: 51,
    };
}

type LocalAwarenessState = {
    activeNode?: {
        nodeId?: string;
        nodeType?: string;
    } | null;
};

export function CommonNodeViewWrapper({
    id,
    label,
    selected,
    decorations,
    presenceUsers,
    settingsPanel,
    contentClassName = DEFAULT_CONTENT_CLASS,
    containerClassName = '',
    containerProps,
    children,
    footer,
    footerClassName = DEFAULT_FOOTER_CLASS,
    position,
    presenceTracking,
    enableFullscreen = true,
    fullscreenContent = undefined,
    fullscreenContainerClassName,
    exitFullscreenRef,
    fullscreenMode = 'portal',
    contentHeightClassName = DEFAULT_CONTENT_HEIGHT,
    fullscreenScope = 'editor',
    isFullscreen: isFullscreenProp,
    onFullscreenChange,
}: CommonNodeViewWrapperProps) {
    const editor = useCurrentEditor()?.editor;
    const [internalFullscreen, setInternalFullscreen] = useState(false);
    const isControlled = isFullscreenProp !== undefined;
    const isFullscreen = isFullscreenProp ?? internalFullscreen;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const wasFullscreenRef = useRef(false);

    const setFullscreen = useCallback(
        (value: SetStateAction<boolean>) => {
            const next = typeof value === 'function' ? value(isFullscreen) : value;
            if (next === isFullscreen) {
                return;
            }
            if (isControlled) {
                onFullscreenChange?.(next);
            } else {
                setInternalFullscreen(next);
            }
        },
        [isControlled, isFullscreen, onFullscreenChange],
    );

    const { contentWrapperRef } = useFullscreenBoundaryContext();
    const useEditorBounds = isFullscreen && fullscreenScope === 'editor';
    const editorBounds = useEditorPaneBounds(contentWrapperRef, useEditorBounds);
    const fullscreenPositionStyle = getFullscreenPositionStyle(fullscreenScope, editorBounds);
    const isFullscreenInline = fullscreenMode === 'inline' && isFullscreen;
    const positionClass =
        fullscreenMode === 'portal' ? 'relative' : isFullscreen ? (fullscreenScope === 'viewport' ? 'fixed' : '') : 'relative';
    const containerClass = cn(
        position ? position : positionClass,
        isFullscreenInline ? 'z-51 flex flex-col rounded-none' : 'rounded border',
        isFullscreenInline && fullscreenScope === 'viewport' ? 'inset-0' : '',
        selected ? 'ring-2 ring-blue-500' : '',
        containerClassName,
    );
    const inlineContentClassName = isFullscreenInline
        ? cn('flex-1 min-h-0 min-w-0 w-full overflow-y-auto', contentClassName)
        : cn('min-w-0', contentClassName, fullscreenMode === 'inline' ? contentHeightClassName : '');

    const collabProvider = presenceTracking?.collabProvider ?? null;
    const trackedNodeId = presenceTracking?.nodeId ?? null;
    const trackedNodeType = presenceTracking?.nodeType;
    const trackedIsActive = Boolean(presenceTracking?.isActive);
    const presenceColor = presenceUsers?.[0]?.color;
    const presenceBoxShadow = presenceColor
        ? selected
            ? `0 0 0 2px #3b82f6, 0 0 0 4px ${presenceColor}`
            : `0 0 0 2px ${presenceColor}`
        : undefined;

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (exitFullscreenRef) {
            exitFullscreenRef.current = () => setFullscreen(false);
        }
    }, [exitFullscreenRef, setFullscreen]);

    useEffect(() => {
        if (!isFullscreen || fullscreenMode !== 'inline') {
            return;
        }
        return registerInlineFullscreen();
    }, [isFullscreen, fullscreenMode]);

    useEffect(() => {
        const isPresenceActive = selected || trackedIsActive;

        if (!collabProvider || !trackedNodeId || !trackedNodeType) {
            return;
        }

        if (!isPresenceActive) {
            const localState = collabProvider.awareness?.getLocalState?.() as LocalAwarenessState | null | undefined;
            const localNode = localState?.activeNode;
            if (localNode?.nodeId === trackedNodeId && localNode?.nodeType === trackedNodeType) {
                collabProvider.awareness?.setLocalStateField('activeNode', null);
            }
            return;
        }

        collabProvider.awareness?.setLocalStateField('activeNode', {
            nodeId: trackedNodeId,
            nodeType: trackedNodeType,
        });

        return () => {
            const localState = collabProvider.awareness?.getLocalState?.() as LocalAwarenessState | null | undefined;
            const localNode = localState?.activeNode;
            if (localNode?.nodeId === trackedNodeId && localNode?.nodeType === trackedNodeType) {
                collabProvider.awareness?.setLocalStateField('activeNode', null);
            }
        };
    }, [collabProvider, trackedIsActive, trackedNodeId, trackedNodeType, selected]);

    useLayoutEffect(() => {
        if (!isFullscreen || fullscreenScope !== 'editor') return;

        const el = contentWrapperRef.current;
        if (!el) return;

        const prevOverflow = el.style.overflow;
        el.style.overflow = 'hidden';
        return () => {
            el.style.overflow = prevOverflow;
        };
    }, [isFullscreen, fullscreenScope, contentWrapperRef]);

    useLayoutEffect(() => {
        const previousFullscreen = wasFullscreenRef.current;

        if (previousFullscreen && !isFullscreen && wrapperRef.current) {
            requestAnimationFrame(() => {
                if (wrapperRef.current) {
                    reflowImportedWidthTables(wrapperRef.current);
                }
            });
        }

        if (previousFullscreen !== isFullscreen && editor) {
            queueMicrotask(() => {
                editor.commands.blur();
            });
        }

        wasFullscreenRef.current = isFullscreen;
    }, [isFullscreen, editor]);

    if (!fullscreenContainerClassName) {
        fullscreenContainerClassName = DEFAULT_FULLSCREEN_CLASS;
    }

    const fullscreenOverlay =
        isFullscreen &&
        fullscreenMode === 'portal' &&
        createPortal(
            <div
                className={fullscreenContainerClassName}
                style={{ ...fullscreenPositionStyle, zIndex: PORTAL_FULLSCREEN_Z_INDEX }}
                contentEditable={false}
            >
                <ZyFullscreenToggle
                    isFullscreen={isFullscreen}
                    setIsFullscreen={setFullscreen}
                    className="absolute top-0.5 right-0.5"
                />
                {settingsPanel}
                <div className="h-[calc(100vh-53px)] w-full overflow-hidden">{fullscreenContent}</div>
            </div>,
            document.body,
        );

    return (
        <>
            <ZyNodeViewWrapper
                id={id}
                decorations={decorations}
                className={NODE_VIEW_BACKGROUND_CLASS}
            >
                <div
                    ref={wrapperRef}
                    className={containerClass}
                    {...containerProps}
                    style={{
                        ...(isFullscreenInline ? fullscreenPositionStyle : undefined),
                        ...(presenceBoxShadow ? { boxShadow: presenceBoxShadow } : undefined),
                    }}
                >
                    <NodePresenceIndicator users={presenceUsers ?? []} />
                    {enableFullscreen && fullscreenContent !== undefined && (
                        <ZyFullscreenToggle
                            isFullscreen={isFullscreen}
                            setIsFullscreen={setFullscreen}
                            className="absolute top-0.5 right-0.5"
                        />
                    )}
                    {!isFullscreenInline && (
                        <div
                            contentEditable={false}
                            className={LABEL_BADGE_CLASS}
                        >
                            {label}
                        </div>
                    )}
                    {(!isFullscreen || fullscreenMode !== 'portal') && settingsPanel}
                    <div className={inlineContentClassName}>{!(isFullscreen && fullscreenMode === 'portal') ? children : null}</div>
                    {footer != null ? (
                        <div
                            className={footerClassName}
                            contentEditable={false}
                        >
                            {footer}
                        </div>
                    ) : null}
                </div>
            </ZyNodeViewWrapper>
            {fullscreenOverlay}
        </>
    );
}
